/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * Fast Inquiry Create component
 *
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.36.0
 */

import { useEffect, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import includes from 'lodash/includes';
import { useSelector } from 'react-redux';

import { RadioButton } from '@/main/components/common';
import { EFastActionsButtonTypes } from '@/main/components/FastActions/constants/constants';
import { useGetFastActionsQuery } from '@/main/components/FastActions/store';
import { EFastBasketSwitchboxContext } from '@/main/components/Table/constants';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import { makeSelectTranslationPagePersonalAccount, makeSelectTranslationPageSubscriberMobile } from '@/main/modules/internationalization/intSelectors';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import PagePersonalAccount from '../PagePersonalAccount/PagePersonalAccount';
import PageSubscriberMobile from '../PageSubscriberMobile/PageSubscriberMobile';
import {
    RadioButtonsContainerStyled,
} from './styled/FastInquiryCreateStyled';



enum ERadioButtons {
    ACCOUNTS = 'ACCOUNTS',
    SUBSCRIBERS = 'SUBSCRIBERS'
}

export const FastInquiryCreate = () => {

    const { title: subscriberMobileTitle } = useSelector(makeSelectTranslationPageSubscriberMobile());
    const { title: personalAccountTitle } = useSelector(makeSelectTranslationPagePersonalAccount());

    const [selectedRadioButton, setSelectedRadioButton] = useState(ERadioButtons.SUBSCRIBERS);

    const isSubscribersSelected = selectedRadioButton === ERadioButtons.SUBSCRIBERS;
    const isAccountsSelected = selectedRadioButton === ERadioButtons.ACCOUNTS;

    const { data: fastActionsData, isSuccess: isFastActionsDataSuccess } = useGetFastActionsQuery();

    useEffect(() => {
        if (
            !includes(fastActionsData?.buttons, EFastActionsButtonTypes.INQUIRY_CREATE) &&
            isFastActionsDataSuccess
        ) {
            openRoute(RoutePath.dashboard);
        }
    }, [fastActionsData, isFastActionsDataSuccess]);

    return (
        <>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.pagePersonalAccount,
                            TranslationFileNamesEnum.pageSubscriberMobile,
                        ]}/>
                        
            <Typography className="page-title" variant="h3">
                {subscriberMobileTitle.fastInquiryCreateTitle}
            </Typography>

            <Typography isDangerousInnerHTML variant="body1">
                {subscriberMobileTitle.fastInquiryCreateSubstitle}
            </Typography>

            <RadioButtonsContainerStyled>
                <RadioButton checked={isSubscribersSelected}
                    className="fast-inquiry-create-subscribers__radio-button"
                    label={subscriberMobileTitle.fastActionRadioButton}
                    onChange={() => setSelectedRadioButton(ERadioButtons.SUBSCRIBERS)}
                />
                <RadioButton checked={isAccountsSelected}
                    className="fast-inquiry-create-accounts__radio-button"
                    label={personalAccountTitle.fastActionRadioButton}
                    onChange={() => setSelectedRadioButton(ERadioButtons.ACCOUNTS)}
                />
            </RadioButtonsContainerStyled>

            {
                isSubscribersSelected && 
                <PageSubscriberMobile 
                    isFastActionPage
                    isFastBasketPage
                    context={EFastBasketSwitchboxContext.MAIN_OPERATIONS}
                />
            }
            {
                isAccountsSelected &&
                <PagePersonalAccount 
                    isFastActionPage
                    isFastBasketPage
                    basketContext={EFastBasketSwitchboxContext.MAIN_OPERATIONS}
                />
            }
        </>
    );
};
